/**
 * «Shared with me» of play.makeit.app (PlayHome): the projects played with a
 * code in this browser, newest first, so they can be played again without the
 * code (the Unity app's student mode kept the redeemed projects on the device).
 * Only the code and what the card shows are kept, in localStorage; the project
 * is loaded again by its code each time.
 */
import type { Project } from "@makeit/schema";

export interface SharedCode {
  code: string;
  name: string;
  type: string;
  template: string;
  /** last played, ms */
  at: number;
}

const KEY = "makeit:sharedCodes";
const MAX = 60;

export function loadSharedCodes(): SharedCode[] {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) ?? "[]") as SharedCode[];
    return Array.isArray(list) ? list.filter((s) => s && typeof s.code === "string" && typeof s.name === "string") : [];
  } catch {
    return [];
  }
}

function save(list: SharedCode[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {
    /* private window or full storage: the list is only a convenience */
  }
}

const norm = (code: string) => code.trim();

/** A project played with `code`: first in the list, with its current name and look. */
export function rememberSharedCode(code: string, project: Project): void {
  const c = norm(code);
  const entry: SharedCode = { code: c, name: project.name, type: project.type, template: project.template, at: Date.now() };
  save([entry, ...loadSharedCodes().filter((s) => norm(s.code) !== c)]);
}

export function forgetSharedCode(code: string): void {
  const c = norm(code);
  save(loadSharedCodes().filter((s) => norm(s.code) !== c));
}
